import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import { useReactToPrint } from 'react-to-print';
import { boxApi } from '@/services/box.api';
import { LoadingState } from '@/components/common/LoadingState';
import { ErrorState } from '@/components/common/ErrorState';
import { formatPrice, formatResumePrice } from '@/utils/price.utils';
import type { BoxSummary, BoxSummaryRow } from '@/types';

/** Raggruppa le righe del riepilogo per `group` mantenendo l'ordine di comparsa. */
function groupRows(rows: BoxSummaryRow[]): { group: string; rows: BoxSummaryRow[] }[] {
  const groups: { group: string; rows: BoxSummaryRow[] }[] = [];
  for (const row of rows) {
    let g = groups.find((x) => x.group === row.group);
    if (!g) {
      g = { group: row.group, rows: [] };
      groups.push(g);
    }
    g.rows.push(row);
  }
  return groups;
}

function GroupTable({ group, rows }: { group: string; rows: BoxSummaryRow[] }) {
  const subtotal = rows.reduce((sum, r) => sum + (r.priceDiff ?? 0), 0);
  return (
    <TableContainer sx={{ mt: 2, backgroundColor: '#F9F3EE', '&:first-of-type': { mt: 0 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2 }}>
        <Box
          sx={{ width: 28, height: 28, borderRadius: '50%', border: '2px solid', borderColor: 'secondary.main', flexShrink: 0 }}
        />
        <Typography sx={{ fontWeight: 600, fontSize: 16, textTransform: 'uppercase' }}>{group}</Typography>
      </Box>
      <Table size="small" sx={{ mb: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Voce</TableCell>
            <TableCell />
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              Valore
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i}>
              <TableCell sx={{ textTransform: 'uppercase', fontSize: 13, fontWeight: 500 }}>{row.label}</TableCell>
              <TableCell sx={{ fontSize: 13 }}>{row.selection ?? '—'}</TableCell>
              <TableCell align="right" sx={{ fontSize: 13, whiteSpace: 'nowrap', color: row.priceDiff ? 'primary.main' : 'text.primary' }}>
                {formatResumePrice(row.priceDiff)}
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell />
            <TableCell />
            <TableCell align="right" sx={{ fontWeight: 750, fontSize: 18, color: 'primary.main', whiteSpace: 'nowrap' }}>
              {formatResumePrice(subtotal)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** Riepilogo di una configurazione box (scelte per box/unione + valore totale + stampa). */
export default function BoxResumePage() {
  const { id } = useParams<{ id: string }>();
  const configId = Number(id);
  const navigate = useNavigate();

  const [summary, setSummary] = useState<BoxSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({ contentRef: printRef });

  const load = (): void => {
    setIsLoading(true);
    setError(null);
    boxApi
      .getSummary(configId)
      .then((s) => {
        setSummary(s);
        setIsLoading(false);
      })
      .catch(() => {
        setError('Impossibile caricare il riepilogo box. Riprova.');
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (Number.isNaN(configId)) {
      setError('Configurazione non valida.');
      setIsLoading(false);
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configId]);

  const groups = summary ? groupRows(summary.rows) : [];

  return (
    <Box sx={{ flex: 1, px: { xs: 2, md: 6 }, py: 5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>
            Box auto — riepilogo
          </Typography>
          <Typography variant="h2">{summary?.name ?? '—'}</Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Button variant="outlined" color="primary" startIcon={<EditIcon />} onClick={() => navigate(`/box/${configId}`)}>
            Modifica le tue scelte
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PrintIcon />}
            disabled={isLoading || error != null}
            onClick={() => handlePrint()}
          >
            Stampa / Scarica PDF
          </Button>
        </Stack>
      </Box>

      {isLoading && <LoadingState message="Caricamento riepilogo box…" />}
      {error && <ErrorState message={error} onRetry={load} />}

      {!isLoading && !error && summary && (
        <Box ref={printRef}>
          {groups.length === 0 ? (
            <Typography variant="body1" color="text.secondary">
              Non hai ancora effettuato scelte per questa configurazione.
            </Typography>
          ) : (
            <Stack spacing={0}>
              {groups.map((g) => (
                <GroupTable key={g.group} group={g.group} rows={g.rows} />
              ))}
            </Stack>
          )}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" sx={{ textTransform: 'uppercase' }} color="text.secondary">
              Il <strong>valore totale</strong> delle scelte da te effettuate è di
            </Typography>
            <Typography sx={{ mt: 1, fontWeight: 750, fontSize: 30, textTransform: 'uppercase', color: 'primary.main' }}>
              {formatPrice(summary.total)}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
